import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import {
  checkServerHealth,
  DEFAULT_LAN_URL,
  DEFAULT_TAILSCALE_URL,
  DEFAULT_PUBLIC_URL,
  fetchRoutingConfig,
  updateRoutingConfig,
  RoutingInfo
} from '../services/api';
import {
  X,
  Wifi,
  Radio,
  Server,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Zap,
  Check,
  Cpu,
  Layers,
  Sparkles,
  Download,
  Loader2,
  Rocket,
  Shield,
  Terminal,
  Key,
  Smartphone,
  Cloud,
  Lock,
  Plane,
  ChevronDown,
  ChevronUp,
  BatteryCharging,
  Gauge,
  Compass
} from 'lucide-react';
import { triggerHaptic } from '../services/purrSynthesizer';
import { connectionManager } from '../services/connectionManager';
import {
  checkShizuku,
  requestShizukuPermission,
  execShizuku,
  ShizukuStatus
} from '../services/shizuku';
import { EdgeExecutionMode } from '../services/edgeModelAdapter';
import {
  getBastetStatus,
  startBastetService,
  stopBastetService,
  readMagnetometer,
  getDiagnosticSnapshot,
  pollNfcTag,
  sendUsbTxRx,
  BastetStatusResult,
  BastetDiagnosticSnapshot,
  NfcTagInfo,
  MagnetometerData
} from '../services/bastetBridge';

export const SettingsModal: React.FC = () => {
  const {
    settingsOpen,
    setSettingsOpen,
    serverUrl,
    setServerUrl,
    isOnline,
    networkMode,
    setNetworkPreset,
    recheckNetworkRoute,
    currentVersion,
    serverVersion,
    isUpdateAvailable,
    checkForUpdates,
    installUpdate,
    otaState,
    openOtaSettings,
    authUser,
    isAuthenticated,
    profile,
    edgeMode,
    setEdgeMode,
    edgeModel,
    setEdgeModel,
    edgeProbe,
    recheckEdgeModel,
    availableEdgeModels
  } = useApp();

  const isAdmin = authUser?.role === 'admin' || (profile === 'echo' && isAuthenticated);

  const [modalTab, setModalTab] = useState<'routing' | 'network' | 'updates' | 'shizuku' | 'bastet'>('routing');
  const [aiSubTab, setAiSubTab] = useState<'cloud' | 'edge'>('cloud');
  const [customInput, setCustomInput] = useState(serverUrl);
  const [testState, setTestState] = useState<{ testing: boolean; ok?: boolean; latency?: number } | null>(null);

  // Routing State
  const [routingInfo, setRoutingInfo] = useState<RoutingInfo | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<'gemini' | 'hybrid' | 'openrouter' | 'ollama'>('gemini');
  const [selectedGeminiModel, setSelectedGeminiModel] = useState<string>('gemini-4-argon');
  const [loadingRouting, setLoadingRouting] = useState<boolean>(false);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);

  // Edge Local Model State
  const [probingEdge, setProbingEdge] = useState<boolean>(false);
  const [expandedGuide, setExpandedGuide] = useState<string | null>('privacy');

  // Updates State
  const [checkingUpdate, setCheckingUpdate] = useState<boolean>(false);

  // Shizuku State
  const [shizukuStatus, setShizukuStatus] = useState<ShizukuStatus | null>(null);
  const [shizukuLoading, setShizukuLoading] = useState<boolean>(false);
  const [shizukuShellOutput, setShizukuShellOutput] = useState<string | null>(null);

  // Bastet Sovereign HAL State
  const [bastetStatus, setBastetStatus] = useState<BastetStatusResult | null>(null);
  const [bastetLoading, setBastetLoading] = useState<boolean>(false);
  const [diagnosticSnapshot, setDiagnosticSnapshot] = useState<BastetDiagnosticSnapshot | null>(null);
  const [nfcPolling, setNfcPolling] = useState<boolean>(false);
  const [lastNfcTag, setLastNfcTag] = useState<NfcTagInfo | null>(null);
  const [usbCommand, setUsbCommand] = useState<string>('version');
  const [usbFraming, setUsbFraming] = useState<'raw' | 'line' | 'json_lines' | 'cobs'>('line');
  const [usbProfile, setUsbProfile] = useState<'generic' | 'esp32_console' | 'logic_sniffer' | 'rp2040_picoprobe' | 'bus_pirate'>('generic');
  const [usbOutput, setUsbOutput] = useState<string>('');
  const [usbExecuting, setUsbExecuting] = useState<boolean>(false);

  const refreshShizuku = async () => {
    setShizukuLoading(true);
    try {
      const status = await checkShizuku();
      setShizukuStatus(status);
    } catch {
      setShizukuStatus(null);
    } finally {
      setShizukuLoading(false);
    }
  };

  const handleRequestShizuku = async () => {
    triggerHaptic(20);
    setShizukuLoading(true);
    try {
      const granted = await requestShizukuPermission();
      await refreshShizuku();
      if (granted) {
        setSaveStatus('🐾 Shizuku Permission Granted!');
        setTimeout(() => setSaveStatus(null), 3000);
      }
    } catch (err: any) {
      setSaveStatus(`Request failed: ${err.message || err}`);
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setShizukuLoading(false);
    }
  };

  const handleTestShizukuShell = async () => {
    triggerHaptic(25);
    setShizukuLoading(true);
    try {
      const res = await execShizuku('id && echo "---" && uname -a && echo "🐾 Shizuku ADB Bridge Active"');
      if (res.ok) {
        setShizukuShellOutput(res.output);
      } else {
        setShizukuShellOutput(`Exit code: ${res.exitCode}\nError: ${res.error || res.output || 'Unknown error'}`);
      }
    } catch (err: any) {
      setShizukuShellOutput(`Execution Exception: ${err.message}`);
    } finally {
      setShizukuLoading(false);
    }
  };

  const refreshBastet = async () => {
    setBastetLoading(true);
    try {
      const status = await getBastetStatus();
      setBastetStatus(status);
      const snap = await getDiagnosticSnapshot();
      setDiagnosticSnapshot(snap);
    } catch (err: any) {
      console.warn('[SettingsModal] Bastet refresh error:', err);
    } finally {
      setBastetLoading(false);
    }
  };

  const handleToggleBastetService = async () => {
    triggerHaptic(20);
    setBastetLoading(true);
    try {
      if (bastetStatus?.running) {
        await stopBastetService();
        setSaveStatus('🐾 Bastet Daemon Stopped');
      } else {
        await startBastetService();
        setSaveStatus('🐾 Bastet Daemon Started on :8777');
      }
      await refreshBastet();
      setTimeout(() => setSaveStatus(null), 2500);
    } catch (err: any) {
      setSaveStatus(`Error: ${err.message}`);
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setBastetLoading(false);
    }
  };

  const handleSweepMagnetometer = async () => {
    triggerHaptic(15);
    try {
      const mag = await readMagnetometer();
      setDiagnosticSnapshot(prev => prev ? { ...prev, magnetometer: mag } : null);
      setSaveStatus(`🐾 Magnetic Sweep: ${mag.magnitude_uT} µT (${mag.detection_state || 'STABLE'})`);
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (err: any) {
      setSaveStatus(`Sweep error: ${err.message}`);
      setTimeout(() => setSaveStatus(null), 2000);
    }
  };

  const handlePollNfc = async () => {
    triggerHaptic(20);
    setNfcPolling(true);
    try {
      const res = await pollNfcTag(4000);
      if (res.tagFound && res.tag) {
        setLastNfcTag(res.tag);
        triggerHaptic([30, 40, 50]);
        setSaveStatus(`🐾 Tag Detected: ${res.tag.uid}`);
      } else {
        setSaveStatus(res.message || 'No tag detected in field');
      }
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err: any) {
      setSaveStatus(`NFC error: ${err.message}`);
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setNfcPolling(false);
    }
  };

  const handleExecuteUsbTxRx = async () => {
    triggerHaptic(20);
    setUsbExecuting(true);
    try {
      const res = await sendUsbTxRx({
        payload: usbCommand,
        framing: usbFraming,
        profile: usbProfile,
        timeoutMs: 2500
      });
      if (res.success) {
        setUsbOutput(`[TX: ${res.transmitted.trim()}]\n[RX (${res.bytes_received}B, ${res.duration_ms}ms)]:\n${res.response}`);
      } else {
        setUsbOutput(`[ERROR (${res.duration_ms}ms)]: ${res.error || 'Failed'}\n[RAW]: ${res.raw_response || ''}`);
      }
    } catch (err: any) {
      setUsbOutput(`[EXCEPTION]: ${err.message}`);
    } finally {
      setUsbExecuting(false);
    }
  };

  // Fetch routing on modal open
  useEffect(() => {
    if (settingsOpen) {
      setCustomInput(serverUrl);
      loadRouting();
      refreshShizuku();
    }
  }, [settingsOpen, serverUrl]);

  const loadRouting = async () => {
    setLoadingRouting(true);
    const info = await fetchRoutingConfig();
    if (info && info.current) {
      setRoutingInfo(info);
      setSelectedProvider(info.current.provider);
      if (info.current.gemini_model) {
        setSelectedGeminiModel(info.current.gemini_model);
      }
    }
    setLoadingRouting(false);
  };

  const handleTestNetwork = async (urlToTest: string) => {
    setTestState({ testing: true });
    triggerHaptic(20);
    const result = await checkServerHealth(urlToTest);
    setTestState({ testing: false, ok: result.ok, latency: result.latencyMs });
  };

  const handleSaveNetwork = () => {
    setServerUrl(customInput);
    triggerHaptic([30, 40, 30]);
    setSettingsOpen(false);
  };

  const handleSelectProvider = async (provider: 'gemini' | 'hybrid' | 'openrouter' | 'ollama') => {
    setSelectedProvider(provider);
    triggerHaptic(25);
    setSaveStatus(`Applying ${provider}...`);
    const res = await updateRoutingConfig({
      provider,
      gemini_model: selectedGeminiModel
    });
    if (res.ok) {
      triggerHaptic([30, 50, 30]);
      setSaveStatus(
        provider === 'gemini'
          ? '⚡ Gemini Direct Active'
          : provider === 'hybrid'
          ? '🔄 Hybrid Auto Active'
          : provider === 'openrouter'
          ? '🌐 OpenRouter Active'
          : '🦙 Ollama Local Active'
      );
      loadRouting();
      setTimeout(() => setSaveStatus(null), 3000);
    } else {
      setSaveStatus('Failed to update server');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handleSelectGeminiModel = async (model: string) => {
    setSelectedGeminiModel(model);
    triggerHaptic(25);
    setSaveStatus(`Applying ${model}...`);
    const res = await updateRoutingConfig({
      provider: selectedProvider,
      gemini_model: model
    });
    if (res.ok) {
      triggerHaptic([20, 30]);
      setSaveStatus(`Model: ${model}`);
      loadRouting();
      setTimeout(() => setSaveStatus(null), 3000);
    } else {
      setSaveStatus('Failed to update model');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  if (!settingsOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-150">
      <div className="w-full max-w-sm rounded-2xl glass-panel-gold border border-fissal-gold/40 shadow-2xl p-4 text-slate-100 flex flex-col gap-3.5 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 pb-2.5">
          <div className="flex items-center gap-2">
            <Server className="w-5 h-5 text-fissal-gold" />
            <div>
              <h2 className="font-cinzel text-xs font-bold tracking-wider text-white">CASTLE CONTROL NODE</h2>
              <span className="text-[10px] text-slate-400 font-mono">echodedicated (:8092)</span>
            </div>
          </div>
          <button
            onClick={() => setSettingsOpen(false)}
            className="p-1 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Toggle Pills (5 columns) */}
        <div className="grid grid-cols-5 gap-1 p-1 bg-black/40 rounded-xl border border-white/10">
          <button
            onClick={() => {
              setModalTab('routing');
              triggerHaptic(15);
            }}
            className={`py-1.5 px-1 rounded-lg text-[10px] font-semibold flex items-center justify-center gap-1 transition-all ${
              modalTab === 'routing'
                ? 'bg-gradient-to-r from-amber-500/30 to-amber-600/30 border border-fissal-gold/50 text-fissal-goldLight font-bold shadow-sm'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Zap className="w-3 h-3 text-fissal-gold" />
            <span className="truncate">Routing</span>
          </button>

          <button
            onClick={() => {
              setModalTab('network');
              triggerHaptic(15);
            }}
            className={`py-1.5 px-1 rounded-lg text-[10px] font-semibold flex items-center justify-center gap-1 transition-all ${
              modalTab === 'network'
                ? 'bg-sky-500/20 border border-sky-400/50 text-sky-300 font-bold shadow-sm'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Radio className="w-3 h-3 text-sky-400" />
            <span className="truncate">Network</span>
          </button>

          <button
            onClick={() => {
              setModalTab('updates');
              triggerHaptic(15);
            }}
            className={`py-1.5 px-1 rounded-lg text-[10px] font-semibold flex items-center justify-center gap-1 transition-all relative ${
              modalTab === 'updates'
                ? 'bg-emerald-500/20 border border-emerald-400/50 text-emerald-300 font-bold shadow-sm'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Download className="w-3 h-3 text-emerald-400" />
            <span className="truncate">Updates</span>
            {isUpdateAvailable && (
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-ping" />
            )}
          </button>

          {isAdmin && (
            <button
              onClick={() => {
                setModalTab('shizuku');
                triggerHaptic(15);
                refreshShizuku();
              }}
              className={`py-1.5 px-1 rounded-lg text-[10px] font-semibold flex items-center justify-center gap-1 transition-all relative ${
                modalTab === 'shizuku'
                  ? 'bg-purple-500/20 border border-purple-400/50 text-purple-300 font-bold shadow-sm'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Shield className="w-3 h-3 text-purple-400" />
              <span className="truncate">Shizuku</span>
              {shizukuStatus?.granted && (
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              )}
            </button>
          )}

          {isAdmin && (
            <button
              onClick={() => {
                setModalTab('bastet');
                triggerHaptic(15);
                refreshBastet();
              }}
              className={`py-1.5 px-1 rounded-lg text-[10px] font-semibold flex items-center justify-center gap-1 transition-all relative ${
                modalTab === 'bastet'
                  ? 'bg-amber-500/20 border border-amber-400/50 text-amber-300 font-bold shadow-sm'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Compass className="w-3 h-3 text-amber-400" />
              <span className="truncate">Bastet</span>
              {bastetStatus?.running && (
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              )}
            </button>
          )}
        </div>

        {/* Status Toast */}
        {saveStatus && (
          <div className="px-3 py-1.5 rounded-lg bg-fissal-gold/20 border border-fissal-gold/50 text-fissal-goldLight text-xs font-semibold flex items-center gap-2 animate-in fade-in slide-in-from-top-2 duration-150">
            <Sparkles className="w-3.5 h-3.5 text-fissal-gold" />
            <span>{saveStatus}</span>
          </div>
        )}

        {/* TAB 1: AI ROUTING & MODELS */}
        {modalTab === 'routing' && (
          <div className="flex-1 overflow-y-auto space-y-3 pr-0.5 no-scrollbar">
            {/* Sub-Navigation Switcher: Cloud & Castle vs On-Device Edge */}
            <div className="grid grid-cols-2 p-1 bg-black/50 rounded-xl border border-white/10 gap-1">
              <button
                type="button"
                onClick={() => {
                  setAiSubTab('cloud');
                  triggerHaptic(15);
                }}
                className={`py-1.5 px-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
                  aiSubTab === 'cloud'
                    ? 'bg-gradient-to-r from-amber-500/25 to-amber-600/25 border border-fissal-gold/50 text-fissal-goldLight font-bold shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Cloud className="w-3.5 h-3.5" />
                <span>Cloud & Castle</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setAiSubTab('edge');
                  triggerHaptic(15);
                }}
                className={`py-1.5 px-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all relative ${
                  aiSubTab === 'edge'
                    ? 'bg-gradient-to-r from-emerald-500/25 to-teal-500/25 border border-emerald-400/50 text-emerald-300 font-bold shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Smartphone className="w-3.5 h-3.5 text-emerald-400" />
                <span>On-Device Edge</span>
                {edgeProbe?.available && (
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                )}
              </button>
            </div>

            {/* SUBTAB A: CLOUD & CASTLE AI ROUTING */}
            {aiSubTab === 'cloud' && (
              <div className="space-y-3 animate-in fade-in duration-150">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-slate-300 uppercase tracking-wider flex items-center justify-between">
                    <span>Castle Server & Cloud Routing</span>
                    {loadingRouting && <RefreshCw className="w-3 h-3 animate-spin text-fissal-gold" />}
                  </label>

                  {/* 1. Google Gemini Direct */}
                  <button
                    type="button"
                    onClick={() => handleSelectProvider('gemini')}
                    className={`w-full text-left p-3 rounded-xl border transition-all ${
                      selectedProvider === 'gemini'
                        ? 'bg-gradient-to-r from-amber-500/20 via-slate-900 to-black/80 border-fissal-gold shadow-md'
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-lg bg-amber-500/20 border border-fissal-gold/40 flex items-center justify-center text-fissal-gold">
                          <Zap className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="text-xs font-bold text-white flex items-center gap-1.5">
                            Google Gemini Direct ⚡
                          </div>
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            Ultra-fast direct API with Fido Interactions retriever hound.
                          </div>
                        </div>
                      </div>
                      {selectedProvider === 'gemini' && (
                        <div className="w-5 h-5 rounded-full bg-fissal-gold text-slate-950 flex items-center justify-center shrink-0">
                          <Check className="w-3.5 h-3.5 stroke-[3]" />
                        </div>
                      )}
                    </div>
                  </button>

                  {/* 2. Hybrid Auto */}
                  <button
                    type="button"
                    onClick={() => handleSelectProvider('hybrid')}
                    className={`w-full text-left p-3 rounded-xl border transition-all ${
                      selectedProvider === 'hybrid'
                        ? 'bg-gradient-to-r from-sky-500/20 via-slate-900 to-black/80 border-sky-400 shadow-md'
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-lg bg-sky-500/20 border border-sky-400/40 flex items-center justify-center text-sky-400">
                          <Layers className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="text-xs font-bold text-white flex items-center gap-1.5">
                            Hybrid Auto Failover 🔄
                          </div>
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            Gemini primary with graceful fallback to OpenRouter & Ollama.
                          </div>
                        </div>
                      </div>
                      {selectedProvider === 'hybrid' && (
                        <div className="w-5 h-5 rounded-full bg-sky-400 text-slate-950 flex items-center justify-center shrink-0">
                          <Check className="w-3.5 h-3.5 stroke-[3]" />
                        </div>
                      )}
                    </div>
                  </button>

                  {/* 3. OpenRouter Cloud */}
                  <button
                    type="button"
                    onClick={() => handleSelectProvider('openrouter')}
                    className={`w-full text-left p-3 rounded-xl border transition-all ${
                      selectedProvider === 'openrouter'
                        ? 'bg-gradient-to-r from-indigo-500/20 via-slate-900 to-black/80 border-indigo-400 shadow-md'
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-lg bg-indigo-500/20 border border-indigo-400/40 flex items-center justify-center text-indigo-400">
                          <Cpu className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="text-xs font-bold text-white flex items-center gap-1.5">
                            OpenRouter Gateway 🌐
                          </div>
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            Multi-provider cloud gateway fallback (gemini-4-argon).
                          </div>
                        </div>
                      </div>
                      {selectedProvider === 'openrouter' && (
                        <div className="w-5 h-5 rounded-full bg-indigo-400 text-slate-950 flex items-center justify-center shrink-0">
                          <Check className="w-3.5 h-3.5 stroke-[3]" />
                        </div>
                      )}
                    </div>
                  </button>

                  {/* 4. Local Ollama */}
                  <button
                    type="button"
                    onClick={() => handleSelectProvider('ollama')}
                    className={`w-full text-left p-3 rounded-xl border transition-all ${
                      selectedProvider === 'ollama'
                        ? 'bg-gradient-to-r from-emerald-500/20 via-slate-900 to-black/80 border-emerald-400 shadow-md'
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-lg bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-400">
                          <Sparkles className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="text-xs font-bold text-white flex items-center gap-1.5">
                            Server Ollama 🦙
                          </div>
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            Host-native offline models on echodedicated (gemma4:e2b).
                          </div>
                        </div>
                      </div>
                      {selectedProvider === 'ollama' && (
                        <div className="w-5 h-5 rounded-full bg-emerald-400 text-slate-950 flex items-center justify-center shrink-0">
                          <Check className="w-3.5 h-3.5 stroke-[3]" />
                        </div>
                      )}
                    </div>
                  </button>
                </div>

                {/* Gemini Model Selector (Shown for Gemini & Hybrid) */}
                {(selectedProvider === 'gemini' || selectedProvider === 'hybrid') && (
                  <div className="space-y-1.5 pt-1 border-t border-white/10">
                    <label className="text-[11px] font-semibold text-slate-300 flex items-center justify-between">
                      <span>Gemini Model Tier</span>
                      <span className="text-[10px] font-mono text-fissal-gold">{selectedGeminiModel}</span>
                    </label>
                    <div className="grid grid-cols-3 gap-1.5">
                      {[
                        { id: 'gemini-4-argon', label: '4 Argon 💎', desc: 'Primary SOTA Model' },
                        { id: 'gemini-3.8-flash', label: '3.8 Flash ⚡', desc: 'Fast / Lightweight' }
                      ].map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => handleSelectGeminiModel(m.id)}
                          className={`p-2 rounded-xl border text-left transition-all ${
                            selectedGeminiModel === m.id
                              ? 'bg-amber-500/20 border-fissal-gold text-white font-bold'
                              : 'bg-white/5 border-white/10 text-slate-400 hover:text-white'
                          }`}
                        >
                          <div className="text-[11px] font-bold">{m.label}</div>
                          <div className="text-[9px] text-slate-400 font-mono mt-0.5 truncate">{m.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Fido Built-in Tool Engine Card */}
                <div className="bg-gradient-to-r from-amber-500/10 via-black/40 to-slate-900 border border-fissal-gold/30 rounded-xl p-2.5 space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-white">
                      <span>🐕 Fissal's Fido (Retriever Hound)</span>
                    </div>
                    <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-semibold">
                      BUILT-IN TOOL
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-300 leading-relaxed">
                    Embedded directly as an auxiliary tool for <strong>Gemini 3.8 Flash</strong>. Fido uses Google's Interactions API retrieval engine to index 78+ ecosystem tools on demand without bloating prompt context.
                  </p>
                </div>

                {/* Jumper Banner to On-Device Edge */}
                <button
                  type="button"
                  onClick={() => {
                    setAiSubTab('edge');
                    triggerHaptic(15);
                  }}
                  className="w-full text-left p-2.5 rounded-xl bg-gradient-to-r from-emerald-950/40 via-slate-900 to-black/60 border border-emerald-500/30 hover:border-emerald-500/50 transition-all flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center text-emerald-400 shrink-0">
                      <Smartphone className="w-3.5 h-3.5" />
                    </div>
                    <div>
                      <div className="text-[11px] font-bold text-emerald-300 flex items-center gap-1.5">
                        <span>On-Device Gemma 4 Standby</span>
                        <span className="text-[9px] font-mono px-1 py-0.2 rounded bg-emerald-500/20 border border-emerald-500/40">
                          {edgeMode.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-[9px] text-slate-400">
                        Phone silicon inference ready for offline resilience &amp; privacy.
                      </div>
                    </div>
                  </div>
                  <span className="text-[10px] text-emerald-400 font-semibold font-mono shrink-0">
                    Configure &rarr;
                  </span>
                </button>
              </div>
            )}

            {/* SUBTAB B: ON-DEVICE LOCAL EDGE MODELS */}
            {aiSubTab === 'edge' && (
              <div className="space-y-3 animate-in fade-in duration-150">
                {/* 1. On-Device Hardware & Engine Status Card */}
                <div className="bg-gradient-to-br from-emerald-950/40 via-slate-900 to-black/70 border border-emerald-500/40 rounded-xl p-3 space-y-2 shadow-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-emerald-500/20 border border-emerald-400/50 flex items-center justify-center text-emerald-400 shadow-quantum-glow">
                        <Smartphone className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-white flex items-center gap-1.5">
                          <span>{edgeModel.name}</span>
                          <span className="text-[9px] font-mono px-1.5 py-0.2 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-semibold">
                            EDGE SILICON
                          </span>
                        </div>
                        <div className="text-[10px] text-slate-400 font-mono">
                          {edgeModel.runtimeType}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                      </span>
                      <span className="text-[10px] font-mono text-emerald-400 font-semibold">
                        {edgeProbe?.available ? 'Verified ⚡' : 'Standby'}
                      </span>
                    </div>
                  </div>

                  {/* Hardware & Storage Metrics */}
                  <div className="grid grid-cols-3 gap-1.5 text-center font-mono text-[10px] bg-black/40 rounded-lg p-2 border border-white/5">
                    <div>
                      <div className="text-slate-400 text-[9px]">Model Footprint</div>
                      <div className="text-white font-bold">{edgeProbe?.sizeFormatted || edgeModel.sizeFormatted}</div>
                    </div>
                    <div>
                      <div className="text-slate-400 text-[9px]">Quantization</div>
                      <div className="text-emerald-300 font-semibold truncate">{edgeModel.quantization.split(' ')[0]}</div>
                    </div>
                    <div>
                      <div className="text-slate-400 text-[9px]">Context Limit</div>
                      <div className="text-sky-300 font-semibold">{edgeModel.contextTokens} Tok</div>
                    </div>
                  </div>

                  {/* Storage Path & Permissions */}
                  <div className="space-y-1 text-[10px]">
                    <div className="flex items-center justify-between text-slate-400 font-mono">
                      <span>Model Location:</span>
                      <span className="text-slate-300">Google AI Edge Gallery</span>
                    </div>
                    <div className="p-1.5 rounded bg-black/60 border border-white/5 font-mono text-[9px] text-slate-400 break-all select-all">
                      {edgeModel.modelPath}
                    </div>
                    <div className="flex items-center justify-between text-slate-400 font-mono pt-0.5">
                      <span>Storage Access:</span>
                      <span className={shizukuStatus?.granted ? 'text-emerald-400' : 'text-amber-400'}>
                        {shizukuStatus?.granted ? 'Shizuku Direct Bridge ⚡' : 'Device App Sandbox'}
                      </span>
                    </div>
                  </div>

                  {/* Active Verification Probe Button */}
                  <button
                    type="button"
                    onClick={async () => {
                      triggerHaptic(20);
                      setProbingEdge(true);
                      const res = await recheckEdgeModel(true);
                      setProbingEdge(false);
                      setSaveStatus(res.available ? '🐾 On-Device Model Verified!' : 'Model unverified');
                      setTimeout(() => setSaveStatus(null), 3000);
                    }}
                    disabled={probingEdge}
                    className="w-full py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-[11px] font-semibold flex items-center justify-center gap-1.5 transition-all text-slate-300 hover:text-white active:scale-98"
                  >
                    <RefreshCw className={`w-3 h-3 ${probingEdge ? 'animate-spin text-emerald-400' : ''}`} />
                    <span>{probingEdge ? 'Verifying Phone Storage...' : 'Verify Model File & Storage'}</span>
                  </button>
                </div>

                {/* 2. Execution Mode Selector */}
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-slate-300 uppercase tracking-wider flex items-center justify-between">
                    <span>Edge Execution Mode</span>
                    <span className="text-[10px] font-mono text-emerald-400 font-bold">{edgeMode.toUpperCase()}</span>
                  </label>

                  <div className="space-y-1.5">
                    {/* Auto Failover */}
                    <button
                      type="button"
                      onClick={() => {
                        setEdgeMode('auto');
                        triggerHaptic(20);
                        setSaveStatus('🔄 Auto-Failover Active');
                        setTimeout(() => setSaveStatus(null), 2500);
                      }}
                      className={`w-full text-left p-2.5 rounded-xl border transition-all ${
                        edgeMode === 'auto'
                          ? 'bg-gradient-to-r from-emerald-500/20 via-slate-900 to-black/80 border-emerald-400 text-white shadow-md'
                          : 'bg-white/5 border-white/10 hover:bg-white/10 text-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-lg bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-400 shrink-0">
                            <Layers className="w-3.5 h-3.5" />
                          </div>
                          <div>
                            <div className="text-xs font-bold text-white flex items-center gap-1.5">
                              <span>Smart Auto-Failover</span>
                              <span className="text-[9px] font-mono px-1 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                RECOMMENDED
                              </span>
                            </div>
                            <div className="text-[10px] text-slate-400 mt-0.5">
                              Castle server primary for full tools; seamlessly falls back to on-device Gemma 4 if offline.
                            </div>
                          </div>
                        </div>
                        {edgeMode === 'auto' && (
                          <div className="w-5 h-5 rounded-full bg-emerald-400 text-slate-950 flex items-center justify-center shrink-0">
                            <Check className="w-3.5 h-3.5 stroke-[3]" />
                          </div>
                        )}
                      </div>
                    </button>

                    {/* Edge First */}
                    <button
                      type="button"
                      onClick={() => {
                        setEdgeMode('edge_first');
                        triggerHaptic(20);
                        setSaveStatus('📱 Edge Silicon Priority Active');
                        setTimeout(() => setSaveStatus(null), 2500);
                      }}
                      className={`w-full text-left p-2.5 rounded-xl border transition-all ${
                        edgeMode === 'edge_first'
                          ? 'bg-gradient-to-r from-sky-500/20 via-slate-900 to-black/80 border-sky-400 text-white shadow-md'
                          : 'bg-white/5 border-white/10 hover:bg-white/10 text-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-lg bg-sky-500/20 border border-sky-400/40 flex items-center justify-center text-sky-400 shrink-0">
                            <Smartphone className="w-3.5 h-3.5" />
                          </div>
                          <div>
                            <div className="text-xs font-bold text-white flex items-center gap-1.5">
                              <span>Edge-First (Phone Silicon)</span>
                              <span className="text-[9px] font-mono px-1 py-0.2 rounded bg-sky-500/20 text-sky-300 border border-sky-500/30">
                                ZERO LATENCY
                              </span>
                            </div>
                            <div className="text-[10px] text-slate-400 mt-0.5">
                              Runs on-device Gemma 4 first for instant responses; calls server only for homelab tool actions.
                            </div>
                          </div>
                        </div>
                        {edgeMode === 'edge_first' && (
                          <div className="w-5 h-5 rounded-full bg-sky-400 text-slate-950 flex items-center justify-center shrink-0">
                            <Check className="w-3.5 h-3.5 stroke-[3]" />
                          </div>
                        )}
                      </div>
                    </button>

                    {/* Air-Gapped Sovereign */}
                    <button
                      type="button"
                      onClick={() => {
                        setEdgeMode('sovereign_offline');
                        triggerHaptic(20);
                        setSaveStatus('🛡️ Air-Gapped Sovereign Active');
                        setTimeout(() => setSaveStatus(null), 2500);
                      }}
                      className={`w-full text-left p-2.5 rounded-xl border transition-all ${
                        edgeMode === 'sovereign_offline'
                          ? 'bg-gradient-to-r from-purple-500/20 via-slate-900 to-black/80 border-purple-400 text-white shadow-md'
                          : 'bg-white/5 border-white/10 hover:bg-white/10 text-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-lg bg-purple-500/20 border border-purple-400/40 flex items-center justify-center text-purple-400 shrink-0">
                            <Lock className="w-3.5 h-3.5" />
                          </div>
                          <div>
                            <div className="text-xs font-bold text-white flex items-center gap-1.5">
                              <span>Air-Gapped Sovereign</span>
                              <span className="text-[9px] font-mono px-1 py-0.2 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30">
                                100% OFFLINE
                              </span>
                            </div>
                            <div className="text-[10px] text-slate-400 mt-0.5">
                              Disables network inference completely. Zero data leaves handset; 100% on-device processing.
                            </div>
                          </div>
                        </div>
                        {edgeMode === 'sovereign_offline' && (
                          <div className="w-5 h-5 rounded-full bg-purple-400 text-slate-950 flex items-center justify-center shrink-0">
                            <Check className="w-3.5 h-3.5 stroke-[3]" />
                          </div>
                        )}
                      </div>
                    </button>

                    {/* Disabled (Cloud Only) */}
                    <button
                      type="button"
                      onClick={() => {
                        setEdgeMode('disabled');
                        triggerHaptic(20);
                        setSaveStatus('Cloud Only Active');
                        setTimeout(() => setSaveStatus(null), 2500);
                      }}
                      className={`w-full text-left p-2 rounded-xl border transition-all ${
                        edgeMode === 'disabled'
                          ? 'bg-gradient-to-r from-slate-800 via-slate-900 to-black border-slate-400 text-white shadow-md'
                          : 'bg-white/5 border-white/10 hover:bg-white/10 text-slate-400'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-lg bg-white/10 flex items-center justify-center text-slate-400 shrink-0">
                            <Cloud className="w-3 h-3" />
                          </div>
                          <div>
                            <div className="text-[11px] font-semibold text-slate-300">
                              Cloud Only (Edge Disabled)
                            </div>
                          </div>
                        </div>
                        {edgeMode === 'disabled' && (
                          <div className="w-4 h-4 rounded-full bg-slate-400 text-slate-950 flex items-center justify-center shrink-0">
                            <Check className="w-3 h-3 stroke-[3]" />
                          </div>
                        )}
                      </div>
                    </button>
                  </div>
                </div>

                {/* 3. Available On-Device Models Selection */}
                <div className="space-y-1.5 pt-1 border-t border-white/10">
                  <label className="text-[11px] font-semibold text-slate-300 uppercase tracking-wider flex items-center justify-between">
                    <span>Installed Model Architecture</span>
                    <span className="text-[10px] font-mono text-fissal-gold">{edgeModel.id}</span>
                  </label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {availableEdgeModels.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => {
                          setEdgeModel(m.id);
                          triggerHaptic(20);
                          setSaveStatus(`Selected: ${m.name}`);
                          setTimeout(() => setSaveStatus(null), 2500);
                        }}
                        className={`p-2 rounded-xl border text-left transition-all ${
                          edgeModel.id === m.id
                            ? 'bg-emerald-500/20 border-emerald-400 text-white font-bold shadow-sm'
                            : 'bg-white/5 border-white/10 text-slate-400 hover:text-white'
                        }`}
                      >
                        <div className="text-[11px] font-bold text-white flex items-center justify-between">
                          <span>{m.name}</span>
                          {edgeModel.id === m.id && <Check className="w-3 h-3 text-emerald-400" />}
                        </div>
                        <div className="text-[9px] text-emerald-300 font-mono mt-0.5">{m.sizeFormatted}</div>
                        <div className="text-[8px] text-slate-400 truncate mt-0.5">{m.tag}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* 4. Interactive Capability Guide: "Why Local Models & What They Help With" */}
                <div className="space-y-2 pt-1 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] font-semibold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3 text-fissal-gold" />
                      <span>Why Use On-Device Models?</span>
                    </label>
                    <span className="text-[9px] font-mono text-emerald-400">Core Benefits</span>
                  </div>

                  <div className="space-y-1.5">
                    {/* Card 1: Sovereign Privacy */}
                    <div className="bg-black/40 border border-white/10 rounded-xl p-2.5 transition-all">
                      <button
                        type="button"
                        onClick={() => setExpandedGuide(expandedGuide === 'privacy' ? null : 'privacy')}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-lg bg-purple-500/20 text-purple-400 flex items-center justify-center shrink-0">
                            <Lock className="w-3 h-3" />
                          </div>
                          <span className="text-xs font-bold text-white">Absolute Sovereign Privacy</span>
                        </div>
                        {expandedGuide === 'privacy' ? (
                          <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                        )}
                      </button>
                      {expandedGuide === 'privacy' && (
                        <p className="text-[10px] text-slate-300 leading-relaxed mt-2 pt-2 border-t border-white/5">
                          Zero tokens leave your handset. Personal notes, sensitive thoughts, and private reflections are processed 100% on device silicon with zero cloud logging or third-party telemetry.
                        </p>
                      )}
                    </div>

                    {/* Card 2: Offline Continuity */}
                    <div className="bg-black/40 border border-white/10 rounded-xl p-2.5 transition-all">
                      <button
                        type="button"
                        onClick={() => setExpandedGuide(expandedGuide === 'offline' ? null : 'offline')}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-lg bg-sky-500/20 text-sky-400 flex items-center justify-center shrink-0">
                            <Plane className="w-3 h-3" />
                          </div>
                          <span className="text-xs font-bold text-white">True Offline Continuity</span>
                        </div>
                        {expandedGuide === 'offline' ? (
                          <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                        )}
                      </button>
                      {expandedGuide === 'offline' && (
                        <p className="text-[10px] text-slate-300 leading-relaxed mt-2 pt-2 border-t border-white/5">
                          Never get locked out. In airplane mode, subway tunnels, cellular dead zones, or during homelab power maintenance, Fissal stays fully conversational and responsive on your Pixel.
                        </p>
                      )}
                    </div>

                    {/* Card 3: Zero Latency & Token Fees */}
                    <div className="bg-black/40 border border-white/10 rounded-xl p-2.5 transition-all">
                      <button
                        type="button"
                        onClick={() => setExpandedGuide(expandedGuide === 'latency' ? null : 'latency')}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0">
                            <Zap className="w-3 h-3" />
                          </div>
                          <span className="text-xs font-bold text-white">Zero Latency &amp; Infinite Free Queries</span>
                        </div>
                        {expandedGuide === 'latency' ? (
                          <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                        )}
                      </button>
                      {expandedGuide === 'latency' && (
                        <p className="text-[10px] text-slate-300 leading-relaxed mt-2 pt-2 border-t border-white/5">
                          No HTTP handshakes, no rate limits, and zero API bills. Instant token generation accelerated directly by your Google Tensor G4 TPU and Edge GPU hardware.
                        </p>
                      )}
                    </div>

                    {/* Card 4: Device Telemetry & Shizuku */}
                    <div className="bg-black/40 border border-white/10 rounded-xl p-2.5 transition-all">
                      <button
                        type="button"
                        onClick={() => setExpandedGuide(expandedGuide === 'telemetry' ? null : 'telemetry')}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                            <BatteryCharging className="w-3 h-3" />
                          </div>
                          <span className="text-xs font-bold text-white">Local Device Telemetry</span>
                        </div>
                        {expandedGuide === 'telemetry' ? (
                          <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                        )}
                      </button>
                      {expandedGuide === 'telemetry' && (
                        <p className="text-[10px] text-slate-300 leading-relaxed mt-2 pt-2 border-t border-white/5">
                          When paired with Shizuku, on-device models can read phone battery levels, device thermals, and local storage capacity directly without needing round-trips to The Relay.
                        </p>
                      )}
                    </div>

                    {/* Card 5: Safe Homelab Command Queuing */}
                    <div className="bg-black/40 border border-white/10 rounded-xl p-2.5 transition-all">
                      <button
                        type="button"
                        onClick={() => setExpandedGuide(expandedGuide === 'queuing' ? null : 'queuing')}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-lg bg-indigo-500/20 text-indigo-400 flex items-center justify-center shrink-0">
                            <Server className="w-3 h-3" />
                          </div>
                          <span className="text-xs font-bold text-white">Smart Homelab Command Queuing</span>
                        </div>
                        {expandedGuide === 'queuing' ? (
                          <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                        )}
                      </button>
                      {expandedGuide === 'queuing' && (
                        <p className="text-[10px] text-slate-300 leading-relaxed mt-2 pt-2 border-t border-white/5">
                          What happens if you run a server command (like <code className="text-fissal-gold font-mono">pm2 reload</code> or check camera feeds) while offline? The local model safely validates the request, explains that uplink is held, and stages the action rather than throwing network errors.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB 2: NETWORK TOPOLOGY */}
        {modalTab === 'network' && (
          <div className="flex-1 overflow-y-auto space-y-3 pr-0.5 no-scrollbar">
            {/* Presets */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-semibold text-slate-300 uppercase tracking-wider">
                  Connection Mode
                </label>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-fissal-gold/20 text-fissal-goldLight border border-fissal-gold/30 font-semibold">
                  Active Link: {networkMode}
                </span>
              </div>

              {/* Dynamic Auto-Switch Button */}
              <button
                type="button"
                onClick={async () => {
                  triggerHaptic(20);
                  setNetworkPreset('AUTO');
                  await recheckNetworkRoute();
                  setCustomInput(serverUrl);
                  setSaveStatus('⚡ Smart Link Auto-Switch Active');
                  setTimeout(() => setSaveStatus(null), 2500);
                }}
                className={`w-full p-2.5 rounded-xl border text-left transition-all ${
                  connectionManager.getPresetMode() === 'AUTO'
                    ? 'bg-gradient-to-r from-emerald-500/20 via-slate-900 to-black/80 border-emerald-400 text-white shadow-md'
                    : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-400">
                      <Zap className="w-3.5 h-3.5" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-white flex items-center gap-1.5">
                        Dynamic Auto-Switch (LAN ↔ Tailscale) ⚡
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        Prioritizes Home LAN (&lt;250ms fast timeout) with seamless Tailscale fallback.
                      </div>
                    </div>
                  </div>
                  {connectionManager.getPresetMode() === 'AUTO' && (
                    <div className="w-5 h-5 rounded-full bg-emerald-400 text-slate-950 flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5 stroke-[3]" />
                    </div>
                  )}
                </div>
              </button>

              <div className="grid grid-cols-3 gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    setNetworkPreset('LAN');
                    setCustomInput(DEFAULT_LAN_URL);
                    handleTestNetwork(DEFAULT_LAN_URL);
                  }}
                  className={`p-2 rounded-xl border text-left transition-all ${
                    connectionManager.getPresetMode() === 'LAN'
                      ? 'bg-fissal-gold/20 border-fissal-gold text-fissal-goldLight font-semibold'
                      : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-1 text-[11px] font-bold">
                    <Wifi className="w-3.5 h-3.5 text-fissal-gold shrink-0" /> LAN
                  </div>
                  <div className="text-[9px] text-slate-400 font-mono mt-0.5 truncate">:8092</div>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setNetworkPreset('TAILSCALE');
                    setCustomInput(DEFAULT_TAILSCALE_URL);
                    handleTestNetwork(DEFAULT_TAILSCALE_URL);
                  }}
                  className={`p-2 rounded-xl border text-left transition-all ${
                    connectionManager.getPresetMode() === 'TAILSCALE'
                      ? 'bg-sky-500/20 border-sky-400 text-sky-300 font-semibold'
                      : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-1 text-[11px] font-bold">
                    <Radio className="w-3.5 h-3.5 text-sky-400 shrink-0" /> Tailscale
                  </div>
                  <div className="text-[9px] text-slate-400 font-mono mt-0.5 truncate">100.83...</div>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setNetworkPreset('PUBLIC');
                    setCustomInput(DEFAULT_PUBLIC_URL);
                    handleTestNetwork(DEFAULT_PUBLIC_URL);
                  }}
                  className={`p-2 rounded-xl border text-left transition-all ${
                    connectionManager.getPresetMode() === 'PUBLIC'
                      ? 'bg-emerald-500/20 border-emerald-400 text-emerald-300 font-semibold'
                      : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-1 text-[11px] font-bold">
                    <Cloud className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> Public SSL
                  </div>
                  <div className="text-[9px] text-slate-400 font-mono mt-0.5 truncate">gateway...</div>
                </button>
              </div>
            </div>

            {/* Custom Input */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-300">Host Endpoint URL</label>
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-fissal-gold"
                placeholder="http://100.83.56.28:8092"
              />
            </div>

            {/* Test Connection Button & Status */}
            <div className="flex items-center justify-between bg-black/30 p-2.5 rounded-xl border border-white/10 text-xs">
              <div className="flex items-center gap-2 font-mono">
                {testState?.testing ? (
                  <span className="flex items-center gap-1.5 text-fissal-gold">
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Testing link...
                  </span>
                ) : testState?.ok ? (
                  <span className="flex items-center gap-1.5 text-emerald-400">
                    <CheckCircle className="w-3.5 h-3.5" /> Online ({testState.latency}ms)
                  </span>
                ) : testState?.ok === false ? (
                  <span className="flex items-center gap-1.5 text-rose-400">
                    <AlertTriangle className="w-3.5 h-3.5" /> Unreachable
                  </span>
                ) : (
                  <span className="text-slate-400">Status: {isOnline ? 'Active' : 'Offline'}</span>
                )}
              </div>

              <button
                type="button"
                onClick={() => handleTestNetwork(customInput)}
                className="px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/15 text-[11px] font-semibold transition-all"
              >
                Ping Host
              </button>
            </div>
          </div>
        )}

        {/* TAB 3: SOVEREIGN OTA UPDATES */}
        {modalTab === 'updates' && (
          <div className="flex-1 overflow-y-auto space-y-3 pr-0.5 no-scrollbar">
            {/* Current Status Card */}
            <div
              className={`p-3 rounded-xl border flex items-center gap-3 ${
                isUpdateAvailable
                  ? 'bg-gradient-to-r from-amber-500/15 to-emerald-500/10 border-fissal-gold/50'
                  : 'bg-emerald-500/10 border-emerald-500/30'
              }`}
            >
              <div
                className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                  isUpdateAvailable
                    ? 'bg-amber-500/20 text-fissal-gold shadow-quantum-glow'
                    : 'bg-emerald-500/20 text-emerald-400'
                }`}
              >
                {isUpdateAvailable ? (
                  <Rocket className="w-5 h-5 animate-bounce" />
                ) : (
                  <CheckCircle className="w-5 h-5" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-white">
                  {isUpdateAvailable ? 'New Update Available!' : 'Fissal Prime is Up to Date'}
                </div>
                <div className="text-[10px] text-slate-300 truncate">
                  {isUpdateAvailable
                    ? `Server version v${serverVersion?.version || ''} ready to install`
                    : `Running sovereign version v${currentVersion.version}`}
                </div>
              </div>
            </div>

            {/* Version Manifest Details */}
            <div className="space-y-2 bg-black/40 rounded-xl p-3 border border-white/10 text-xs">
              <div className="flex items-center justify-between pb-2 border-b border-white/10">
                <span className="text-slate-400 text-[11px]">Installed App Version</span>
                <span className="font-mono text-fissal-gold font-semibold">v{currentVersion.version}</span>
              </div>
              <div className="flex items-center justify-between pb-2 border-b border-white/10">
                <span className="text-slate-400 text-[11px]">Client Build Date</span>
                <span className="font-mono text-slate-300 text-[10px]">
                  {new Date(currentVersion.buildTime).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
              <div className="flex items-center justify-between pb-2 border-b border-white/10">
                <span className="text-slate-400 text-[11px]">Homelab Gateway Version</span>
                <span className="font-mono text-emerald-400 font-semibold">
                  {serverVersion ? `v${serverVersion.version}` : 'Checking...'}
                </span>
              </div>
              {serverVersion?.apkSizeFormatted && (
                <div className="flex items-center justify-between pb-2 border-b border-white/10">
                  <span className="text-slate-400 text-[11px]">APK Package Size</span>
                  <span className="font-mono text-slate-300 text-[10px]">{serverVersion.apkSizeFormatted}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-slate-400 text-[11px]">OTA Protocol</span>
                <span className="font-mono text-fissal-azure text-[10px]">Direct Castle APK (No Play Store)</span>
              </div>
            </div>

            {/* Release Notes */}
            {serverVersion?.releaseNotes && (
              <div className="space-y-1 bg-black/30 rounded-xl p-3 border border-white/10">
                <div className="text-[11px] font-semibold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3 text-fissal-gold" /> Release Highlights
                </div>
                <p className="text-[11px] text-slate-300 leading-relaxed font-sans mt-1">
                  {serverVersion.releaseNotes}
                </p>
              </div>
            )}

            {/* Live In-App OTA Download Progress */}
            {otaState.status === 'downloading' && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 space-y-2">
                <div className="flex items-center justify-between text-xs font-bold text-white">
                  <span className="flex items-center gap-1.5">
                    <Download className="w-3.5 h-3.5 text-fissal-gold animate-bounce" />
                    Downloading In-App Update...
                  </span>
                  <span className="font-mono text-amber-400">{otaState.percent}%</span>
                </div>
                <div className="w-full bg-slate-900 rounded-full h-2 overflow-hidden border border-white/10 p-[1px]">
                  <div
                    className="bg-gradient-to-r from-amber-500 via-amber-400 to-emerald-400 h-full rounded-full transition-all duration-150 shadow-[0_0_10px_rgba(245,158,11,0.5)]"
                    style={{ width: `${Math.max(4, otaState.percent)}%` }}
                  />
                </div>
                <div className="text-[10px] text-slate-400 font-mono text-right">
                  {(otaState.bytesDownloaded / (1024 * 1024)).toFixed(1)} MB / {((otaState.totalBytes || 5500000) / (1024 * 1024)).toFixed(1)} MB
                </div>
              </div>
            )}

          </div>
        )}

        {/* TAB 4: SHIZUKU PRIVILEGED SHELL */}
        {isAdmin && modalTab === 'shizuku' && (
          <div className="flex-1 overflow-y-auto space-y-3 pr-0.5 no-scrollbar">
            {/* Shizuku Banner */}
            <div
              className={`p-3 rounded-xl border flex items-center gap-3 ${
                shizukuStatus?.granted
                  ? 'bg-emerald-500/10 border-emerald-500/30'
                  : shizukuStatus?.running
                  ? 'bg-amber-500/15 border-fissal-gold/50'
                  : 'bg-slate-900/60 border-white/10'
              }`}
            >
              <div
                className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                  shizukuStatus?.granted
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : shizukuStatus?.running
                    ? 'bg-amber-500/20 text-fissal-gold shadow-quantum-glow'
                    : 'bg-white/10 text-slate-400'
                }`}
              >
                <Shield className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-white flex items-center gap-1.5">
                  Shizuku Elevated Shell
                  {shizukuStatus?.granted && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-mono">
                      Authorized
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-300 truncate">
                  {shizukuStatus?.granted
                    ? 'Elevated ADB shell execution is active and verified.'
                    : shizukuStatus?.running
                    ? 'Shizuku service detected — awaiting authorization.'
                    : 'Shizuku binder not connected. Start Shizuku app.'}
                </div>
              </div>
            </div>

            {/* Shizuku Details Table */}
            <div className="space-y-2 bg-black/40 rounded-xl p-3 border border-white/10 text-xs">
              <div className="flex items-center justify-between pb-2 border-b border-white/10">
                <span className="text-slate-400 text-[11px]">Service Status</span>
                <span
                  className={`font-mono font-semibold text-[11px] ${
                    shizukuStatus?.running ? 'text-emerald-400' : 'text-rose-400'
                  }`}
                >
                  {shizukuStatus?.running ? 'Running / Binder Connected' : 'Offline / Not Connected'}
                </span>
              </div>
              <div className="flex items-center justify-between pb-2 border-b border-white/10">
                <span className="text-slate-400 text-[11px]">Authorization Status</span>
                <span
                  className={`font-mono font-semibold text-[11px] ${
                    shizukuStatus?.granted ? 'text-emerald-400' : 'text-amber-400'
                  }`}
                >
                  {shizukuStatus?.granted ? 'Granted (API_V23)' : 'Not Authorized'}
                </span>
              </div>
              {shizukuStatus?.version !== undefined && shizukuStatus.version > 0 && (
                <div className="flex items-center justify-between pb-2 border-b border-white/10">
                  <span className="text-slate-400 text-[11px]">Shizuku Server API</span>
                  <span className="font-mono text-slate-300 text-[10px]">v{shizukuStatus.version}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-slate-400 text-[11px]">Provider Authority</span>
                <span className="font-mono text-purple-300 text-[10px]">net.echo.fissal.shizuku</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              {!shizukuStatus?.granted ? (
                <button
                  type="button"
                  onClick={handleRequestShizuku}
                  disabled={shizukuLoading}
                  className="w-full py-2.5 rounded-xl bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-700 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-xs shadow-quantum-glow flex items-center justify-center gap-1.5 transition-all disabled:opacity-50"
                >
                  <Key className="w-3.5 h-3.5" />
                  <span>Request Shizuku Permission</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleTestShizukuShell}
                  disabled={shizukuLoading}
                  className="w-full py-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs shadow-quantum-glow flex items-center justify-center gap-1.5 transition-all disabled:opacity-50"
                >
                  <Terminal className="w-3.5 h-3.5" />
                  <span>{shizukuLoading ? 'Executing ADB Test...' : 'Test Elevated Shell (id)'}</span>
                </button>
              )}

              <button
                type="button"
                onClick={refreshShizuku}
                disabled={shizukuLoading}
                className="w-full py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-semibold flex items-center justify-center gap-1.5 transition-all text-slate-300 hover:text-white"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${shizukuLoading ? 'animate-spin text-purple-400' : ''}`} />
                <span>Refresh Shizuku Status</span>
              </button>
            </div>

            {/* Shell Test Output Console */}
            {shizukuShellOutput && (
              <div className="bg-black/70 rounded-xl p-3 border border-purple-500/30 space-y-1">
                <div className="text-[10px] font-mono text-purple-300 flex items-center gap-1.5">
                  <Terminal className="w-3 h-3" /> Shell Execution Output:
                </div>
                <pre className="text-[10px] font-mono text-emerald-400 whitespace-pre-wrap break-all bg-slate-950/80 p-2 rounded-lg border border-white/5 max-h-32 overflow-y-auto">
                  {shizukuShellOutput}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* TAB 5: PROJECT BASTET SOVEREIGN HAL */}
        {modalTab === 'bastet' && (
          <div className="flex-1 overflow-y-auto space-y-3 pr-0.5 no-scrollbar">
            {/* Status Hero Card */}
            <div className="p-3 rounded-xl bg-black/50 border border-amber-500/30 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  bastetStatus?.running ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                }`}>
                  <Compass className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-bold text-white flex items-center gap-1.5">
                    Project Bastet HAL
                    <span className={`text-[9px] px-1.5 py-0.2 rounded-full font-mono border ${
                      bastetStatus?.running
                        ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                        : 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                    }`}>
                      {bastetStatus?.running ? 'ONLINE :8777' : 'STANDBY'}
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-400 font-mono truncate">
                    127.0.0.1:8777 • Uptime: {bastetStatus?.details?.uptime_seconds || 0}s
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={handleToggleBastetService}
                disabled={bastetLoading}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all border shrink-0 ${
                  bastetStatus?.running
                    ? 'bg-rose-500/20 border-rose-500/40 text-rose-300 hover:bg-rose-500/30'
                    : 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30'
                }`}
              >
                {bastetStatus?.running ? 'Stop Daemon' : 'Start Daemon'}
              </button>
            </div>

            {/* Module B: Environmental & Magnetometer Probe */}
            <div className="p-3 rounded-xl bg-black/40 border border-white/10 space-y-2">
              <div className="flex items-center justify-between text-xs font-bold text-amber-300">
                <div className="flex items-center gap-1.5">
                  <Gauge className="w-3.5 h-3.5 text-amber-400" />
                  <span>Module B: Magnetometer & EMF Sweep</span>
                </div>
                <button
                  type="button"
                  onClick={handleSweepMagnetometer}
                  className="px-2 py-0.5 rounded text-[10px] font-mono bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white"
                >
                  Sweep Now
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 rounded-lg bg-black/60 border border-white/5 space-y-0.5">
                  <div className="text-[10px] text-slate-400">Total Flux Density</div>
                  <div className="text-sm font-mono font-bold text-amber-400">
                    {diagnosticSnapshot?.magnetometer?.magnitude_uT || 46.4} µT
                  </div>
                  <div className="text-[9px] font-mono text-slate-400">
                    Acc: {diagnosticSnapshot?.magnetometer?.accuracy || 'HIGH'}
                  </div>
                </div>
                <div className="p-2 rounded-lg bg-black/60 border border-white/5 space-y-0.5">
                  <div className="text-[10px] text-slate-400">EMF Anomaly State</div>
                  <div className="text-[11px] font-mono font-bold text-emerald-300 truncate">
                    {diagnosticSnapshot?.magnetometer?.detection_state || 'AMBIENT_EARTH_FIELD'}
                  </div>
                  <div className="text-[9px] font-mono text-slate-400">
                    Score: {diagnosticSnapshot?.magnetometer?.emf_anomaly_score ?? 0.05} / 1.0
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between text-[10px] font-mono text-slate-400 px-1">
                <span>X: {diagnosticSnapshot?.magnetometer?.x_uT || 0} µT</span>
                <span>Y: {diagnosticSnapshot?.magnetometer?.y_uT || 0} µT</span>
                <span>Z: {diagnosticSnapshot?.magnetometer?.z_uT || 0} µT</span>
              </div>
            </div>

            {/* Module A: NFC Hardware Inspector */}
            <div className="p-3 rounded-xl bg-black/40 border border-white/10 space-y-2">
              <div className="flex items-center justify-between text-xs font-bold text-sky-300">
                <div className="flex items-center gap-1.5">
                  <Radio className="w-3.5 h-3.5 text-sky-400" />
                  <span>Module A: NFC Hardware Inspector</span>
                </div>
                <button
                  type="button"
                  onClick={handlePollNfc}
                  disabled={nfcPolling}
                  className="px-2 py-0.5 rounded text-[10px] font-mono bg-sky-500/20 hover:bg-sky-500/30 border border-sky-500/40 text-sky-300 disabled:opacity-50 flex items-center gap-1"
                >
                  <RefreshCw className={`w-2.5 h-2.5 ${nfcPolling ? 'animate-spin' : ''}`} />
                  <span>{nfcPolling ? 'Scanning...' : 'Poll Tag'}</span>
                </button>
              </div>

              {lastNfcTag ? (
                <div className="p-2 rounded-lg bg-black/60 border border-sky-500/30 space-y-1 text-xs font-mono">
                  <div className="flex items-center justify-between text-slate-300">
                    <span className="text-slate-400 text-[10px]">UID:</span>
                    <span className="font-bold text-sky-300">{lastNfcTag.uid}</span>
                  </div>
                  <div className="text-[10px] text-slate-400 truncate">
                    Tech: {lastNfcTag.tech_list?.map(t => t.split('.').pop()).join(', ')}
                  </div>
                  {lastNfcTag.details?.ndef && (
                    <div className="text-[10px] text-emerald-300 border-t border-white/5 pt-1 mt-1">
                      NDEF ({lastNfcTag.details.ndef.records_count} records, {lastNfcTag.details.ndef.max_size_bytes}B)
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-2 rounded-lg bg-black/30 border border-dashed border-white/10 text-center text-[10px] text-slate-400 font-mono">
                  Hold NFC transponder (card/tag) to phone back & tap Poll Tag
                </div>
              )}
            </div>

            {/* Module C: USB-C Multi-Protocol Transport */}
            <div className="p-3 rounded-xl bg-black/40 border border-white/10 space-y-2">
              <div className="flex items-center justify-between text-xs font-bold text-purple-300">
                <div className="flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5 text-purple-400" />
                  <span>Module C: USB Serial Multiplexer</span>
                </div>
                <span className="text-[10px] font-mono text-slate-400">
                  {bastetStatus?.details?.usb?.status || 'DISCONNECTED'}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <label className="text-[10px] text-slate-400 block mb-1">Framing</label>
                  <select
                    value={usbFraming}
                    onChange={(e) => setUsbFraming(e.target.value as any)}
                    className="w-full py-1 px-2 rounded-lg bg-black/60 border border-white/10 text-[11px] text-white font-mono focus:outline-none focus:border-purple-400"
                  >
                    <option value="line">LINE (\r\n)</option>
                    <option value="json_lines">JSON_LINES</option>
                    <option value="cobs">COBS (0x00 binary)</option>
                    <option value="raw">RAW</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-slate-400 block mb-1">Target Profile</label>
                  <select
                    value={usbProfile}
                    onChange={(e) => setUsbProfile(e.target.value as any)}
                    className="w-full py-1 px-2 rounded-lg bg-black/60 border border-white/10 text-[11px] text-white font-mono focus:outline-none focus:border-purple-400"
                  >
                    <option value="generic">GENERIC UART</option>
                    <option value="esp32_console">ESP32 Console</option>
                    <option value="logic_sniffer">Logic Sniffer (SUMP)</option>
                    <option value="rp2040_picoprobe">RP2040 Pico</option>
                    <option value="bus_pirate">Bus Pirate</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-1.5">
                <input
                  type="text"
                  value={usbCommand}
                  onChange={(e) => setUsbCommand(e.target.value)}
                  placeholder="Payload (e.g. version, AT, status)..."
                  className="flex-1 py-1.5 px-2.5 rounded-lg bg-black/60 border border-white/10 text-xs font-mono text-white placeholder-slate-500 focus:outline-none focus:border-purple-400"
                />
                <button
                  type="button"
                  onClick={handleExecuteUsbTxRx}
                  disabled={usbExecuting}
                  className="px-3 py-1.5 rounded-lg bg-purple-600/30 hover:bg-purple-600/40 border border-purple-500/50 text-purple-200 text-xs font-semibold disabled:opacity-50 flex items-center gap-1 shrink-0"
                >
                  <Terminal className="w-3 h-3" />
                  <span>{usbExecuting ? 'Sending...' : 'Tx/Rx'}</span>
                </button>
              </div>

              {usbOutput && (
                <pre className="p-2 rounded-lg bg-black/70 border border-white/10 text-[10px] font-mono text-emerald-400 max-h-24 overflow-y-auto whitespace-pre-wrap">
                  {usbOutput}
                </pre>
              )}
            </div>

            {/* Battery Diagnostics Snapshot */}
            {diagnosticSnapshot?.battery && (
              <div className="p-2.5 rounded-xl bg-black/30 border border-white/5 flex items-center justify-between text-[10px] font-mono text-slate-400">
                <div className="flex items-center gap-1.5">
                  <BatteryCharging className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Battery: {diagnosticSnapshot.battery.level_percent}% ({diagnosticSnapshot.battery.temperature_celsius}°C)</span>
                </div>
                <span>{diagnosticSnapshot.battery.power_source} • {diagnosticSnapshot.battery.health}</span>
              </div>
            )}
          </div>
        )}

        {/* Footer Buttons */}
        <div className="flex gap-2 pt-2 border-t border-white/10">
          <button
            type="button"
            onClick={() => setSettingsOpen(false)}
            className="flex-1 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-semibold"
          >
            Close
          </button>
          {modalTab === 'network' ? (
            <button
              type="button"
              onClick={handleSaveNetwork}
              className="flex-1 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-xs shadow-quantum-glow"
            >
              Save & Connect
            </button>
          ) : modalTab === 'updates' ? (
            otaState.status === 'downloading' ? (
              <button
                type="button"
                disabled
                className="flex-1 py-2 rounded-xl bg-amber-500/20 border border-fissal-gold/40 text-fissal-gold font-bold text-xs flex items-center justify-center gap-1.5 cursor-not-allowed opacity-90"
              >
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Downloading ({otaState.percent}%)...
              </button>
            ) : otaState.status === 'ready' ? (
              <button
                type="button"
                onClick={() => {
                  triggerHaptic([30, 50, 30]);
                  installUpdate();
                }}
                className="flex-1 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-slate-950 font-bold text-xs shadow-quantum-glow flex items-center justify-center gap-1.5"
              >
                <CheckCircle className="w-3.5 h-3.5 stroke-[2.5]" /> Ready · Tap to Install
              </button>
            ) : isUpdateAvailable ? (
              <button
                type="button"
                onClick={() => {
                  triggerHaptic([30, 50, 30]);
                  installUpdate();
                }}
                className="flex-1 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-slate-950 font-bold text-xs shadow-quantum-glow flex items-center justify-center gap-1.5"
              >
                <Download className="w-3.5 h-3.5 stroke-[2.5]" /> Install APK Update
              </button>
            ) : (
              <button
                type="button"
                onClick={async () => {
                  triggerHaptic(20);
                  setCheckingUpdate(true);
                  await checkForUpdates();
                  setCheckingUpdate(false);
                }}
                disabled={checkingUpdate}
                className="flex-1 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-xs shadow-quantum-glow flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${checkingUpdate ? 'animate-spin' : ''}`} /> {checkingUpdate ? 'Checking...' : 'Check for Updates'}
              </button>
            )
          ) : modalTab === 'shizuku' ? (
            <button
              type="button"
              onClick={shizukuStatus?.granted ? handleTestShizukuShell : handleRequestShizuku}
              disabled={shizukuLoading}
              className="flex-1 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-xs shadow-quantum-glow flex items-center justify-center gap-1.5 disabled:opacity-50"
            >
              {shizukuStatus?.granted ? (
                <>
                  <Terminal className="w-3.5 h-3.5" /> Test Shell
                </>
              ) : (
                <>
                  <Key className="w-3.5 h-3.5" /> Request Access
                </>
              )}
            </button>
          ) : modalTab === 'bastet' ? (
            <button
              type="button"
              onClick={refreshBastet}
              disabled={bastetLoading}
              className="flex-1 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-xs shadow-quantum-glow flex items-center justify-center gap-1.5 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${bastetLoading ? 'animate-spin' : ''}`} />
              <span>Refresh Bastet HAL</span>
            </button>
          ) : modalTab === 'routing' && aiSubTab === 'edge' ? (
            <button
              type="button"
              onClick={async () => {
                triggerHaptic(20);
                setProbingEdge(true);
                const res = await recheckEdgeModel(true);
                setProbingEdge(false);
                setSaveStatus(res.available ? '🐾 Silicon Model Verified!' : 'Model unverified');
                setTimeout(() => setSaveStatus(null), 2500);
              }}
              disabled={probingEdge}
              className="flex-1 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-slate-950 font-bold text-xs shadow-quantum-glow flex items-center justify-center gap-1.5 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${probingEdge ? 'animate-spin' : ''}`} />
              <span>Verify Silicon</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                triggerHaptic(20);
                loadRouting();
                setSaveStatus('Config reloaded');
                setTimeout(() => setSaveStatus(null), 2000);
              }}
              className="flex-1 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-xs shadow-quantum-glow flex items-center justify-center gap-1"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingRouting ? 'animate-spin' : ''}`} /> Refresh Core
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

